import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Eye, CheckCircle, User, Storefront, HandHeart, Gift, Fingerprint, Shield, ArrowRight } from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IrisAuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('form'); // form, verifying, success
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState(null);
  const [referrerName, setReferrerName] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    identity_type: 'seller'
  });
  const [irisId, setIrisId] = useState(null);
  const [referralApplied, setReferralApplied] = useState(null);

  // Check for referral code in URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      validateReferralCode(refCode);
    }
  }, [searchParams]);

  const validateReferralCode = async (code) => {
    if (!code || code.length < 5) {
      setReferralValid(null);
      setReferrerName('');
      return;
    }
    try {
      const response = await axios.get(`${API}/referral/code/${code.toUpperCase()}`);
      if (response.data.is_valid) {
        setReferralValid(true);
        setReferrerName(response.data.referral_code?.owner_name || '');
      } else {
        setReferralValid(false);
        setReferrerName('');
      }
    } catch (error) {
      setReferralValid(false);
      setReferrerName('');
    }
  };

  const handleReferralCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setReferralCode(code);
    if (code.length >= 10) {
      validateReferralCode(code);
    } else {
      setReferralValid(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.identity_type) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    setStep('verifying');

    // Simulate verification time
    await new Promise(resolve => setTimeout(resolve, 1500));

    try {
      // Register identity
      const response = await axios.post(
        `${API}/iris/register?name=${encodeURIComponent(formData.name)}&identity_type=${formData.identity_type}&email=${encodeURIComponent(formData.email || '')}&phone=${encodeURIComponent(formData.phone || '')}`
      );
      
      if (response.data.success) {
        const newIrisId = response.data.iris_id;
        setIrisId(newIrisId);
        
        // Store in localStorage
        localStorage.setItem('ginsgood_iris_id', newIrisId);
        localStorage.setItem('ginsgood_iris_name', formData.name);
        localStorage.setItem('ginsgood_iris_type', formData.identity_type);

        // Apply referral code if valid
        if (referralValid && referralCode) {
          try {
            const refResponse = await axios.post(
              `${API}/referral/apply?code=${referralCode}&referred_type=${formData.identity_type}&referred_id=${newIrisId}&referred_name=${encodeURIComponent(formData.name)}`
            );
            if (refResponse.data.success) {
              setReferralApplied(refResponse.data);
              toast.success(`Referral applied! ${referrerName} will earn GSG when you certify.`);
            }
          } catch (refError) {
            console.log('Referral apply error:', refError);
          }
        }

        setStep('success');
        toast.success('Identity verified successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
      setStep('form');
    } finally {
      setLoading(false);
    }
  };

  const identityTypes = [
    { value: 'seller', label: 'Seller / Brand', icon: Storefront, description: 'Businesses selling certified products' },
    { value: 'artisan', label: 'Artisan / Craftsman', icon: HandHeart, description: 'Craftspeople creating products' },
    { value: 'customer', label: 'Customer / Buyer', icon: User, description: 'Consumers verifying products' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-lg mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-8">
            <Badge variant="outline" className="mb-4">
              <Fingerprint weight="duotone" className="w-4 h-4 mr-2" />
              Identity Verification
            </Badge>
            <h1 className="text-3xl md:text-4xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {step === 'success' ? 'Welcome to GinsGood!' : 'Create Your Identity'}
            </h1>
            <p className="text-muted-foreground">
              {step === 'success' 
                ? 'Your digital identity has been verified'
                : 'Register to certify products and earn GinsGoodCoin'
              }
            </p>
          </div>

          {/* Registration Form */}
          {step === 'form' && (
            <Card className="p-6" data-testid="registration-form">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Referral Code Banner */}
                {referralValid && referrerName && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                    <Gift weight="duotone" className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">Referred by {referrerName}!</p>
                      <p className="text-sm text-green-600">You'll both earn GSG when you certify your first product</p>
                    </div>
                  </div>
                )}

                {/* Name */}
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your full name"
                    required
                    data-testid="input-name"
                  />
                </div>

                {/* Identity Type */}
                <div>
                  <Label>Identity Type *</Label>
                  <div className="grid grid-cols-1 gap-3 mt-2">
                    {identityTypes.map(type => {
                      const Icon = type.icon;
                      const isSelected = formData.identity_type === type.value;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, identity_type: type.value })}
                          className={`p-4 rounded-lg border-2 text-left flex items-center gap-4 transition-all ${
                            isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                          }`}
                          data-testid={`type-${type.value}`}
                        >
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'
                          }`}>
                            <Icon weight="duotone" className="w-6 h-6" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{type.label}</p>
                            <p className="text-xs text-muted-foreground">{type.description}</p>
                          </div>
                          {isSelected && <CheckCircle weight="fill" className="w-6 h-6 text-primary" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Email */}
                <div>
                  <Label>Email (Optional)</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    data-testid="input-email"
                  />
                </div>

                {/* Referral Code */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Gift className="w-4 h-4" />
                    Referral Code (Optional)
                  </Label>
                  <div className="relative">
                    <Input
                      value={referralCode}
                      onChange={handleReferralCodeChange}
                      placeholder="GSG-XXXX-XXXXXX"
                      className={`pr-10 ${
                        referralValid === true ? 'border-green-500' : 
                        referralValid === false ? 'border-red-500' : ''
                      }`}
                      data-testid="input-referral"
                    />
                    {referralValid === true && (
                      <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                    )}
                  </div>
                  {referralValid === false && (
                    <p className="text-xs text-red-500 mt-1">Invalid referral code</p>
                  )}
                  {referralValid === true && referrerName && (
                    <p className="text-xs text-green-600 mt-1">Referred by {referrerName}</p>
                  )}
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 rounded-full gap-2"
                  disabled={loading}
                  data-testid="submit-btn"
                >
                  {loading ? 'Verifying...' : 'Create Identity'}
                  <ArrowRight weight="bold" className="w-5 h-5" />
                </Button>
              </form>
            </Card>
          )}

          {/* Verifying State */}
          {step === 'verifying' && (
            <Card className="p-12 text-center">
              <div className="w-24 h-24 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-6 animate-pulse">
                <Shield weight="duotone" className="w-12 h-12 text-primary" />
              </div>
              <h2 className="text-xl font-medium mb-2">Verifying Identity...</h2>
              <p className="text-muted-foreground">Creating your secure digital identity</p>
            </Card>
          )}

          {/* Success State */}
          {step === 'success' && (
            <Card className="p-8 text-center" data-testid="success-card">
              <div className="w-24 h-24 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle weight="duotone" className="w-12 h-12 text-green-600" />
              </div>
              
              <h2 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Identity Verified!
              </h2>
              <p className="text-muted-foreground mb-6">
                Welcome, {formData.name}
              </p>

              <div className="bg-muted rounded-lg p-4 mb-6">
                <p className="text-xs text-muted-foreground mb-1">Your Identity ID</p>
                <code className="text-sm font-mono break-all">{irisId}</code>
              </div>

              {referralApplied && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <Gift weight="duotone" className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-green-800 font-medium">Referral Applied!</p>
                  <p className="text-sm text-green-600">
                    {referrerName} will earn GSG when you complete your first certification
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-3">
                <Button onClick={() => navigate('/certify')} className="w-full gap-2" data-testid="go-certify-btn">
                  Certify a Product
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button onClick={() => navigate('/scan')} variant="outline" className="w-full">
                  Scan & Verify Products
                </Button>
                <Button onClick={() => navigate('/wallet')} variant="ghost" className="w-full">
                  View Your Wallet
                </Button>
              </div>
            </Card>
          )}

          {/* Info */}
          <div className="mt-8 text-center text-sm text-muted-foreground">
            <p>Your identity is secured using blockchain technology</p>
            <p>No camera access required</p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default IrisAuthPage;
